/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {  useEffect, useRef } from 'react';
import {
    string, shape, arrayOf, number, func,
} from 'prop-types';
import Slider from 'react-slick';
import ReactHtmlParser from 'react-html-parser';
import { useUIDSeed } from 'react-uid';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useDispatch, useSelector } from 'react-redux';
import Media from 'react-media';
import style from './SelectedGiftSize.module.css';
import byoTheme from './BYOTheme';
import { getBrand, getFeatureFlag } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getSelectedBYOItems } from '../../../../../../../../state/ducks/App/App-Selectors';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import ResponsiveImage from '../../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import useSSRMediaQuery from '../../../../../../../helpers/hooks/useSSRMediaQuery';
import useSalesforcePersonalizedContentQuery from '../../../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import GenericSkeleton from '../../../../../GraphqlSkeletonComponents/GenericSkeleton';

const SelectGiftSize = ({
    skuData,
    selectProduct,
    renderSkuPrice,
}) => {
    const sliderRef = useRef(null);
    useStyles(style);
    const dispatch = useDispatch();
    const brandData = useSelector(getBrand);
    const brandCode = brandData?.code;
    const selectedItems = useSelector(getSelectedBYOItems);
    const deviceType = useSSRMediaQuery();
    const pageType = useSelector(getCurrentPageType);
    const pdpCountCyoButtonFlag = useSelector(getFeatureFlag('is-pdp-add-count-to-select-cyo-button-ab-test-enabled'));
    const isForcePdpCountCyoButtonFlag = useSelector(getFeatureFlag('is-force-pdp-add-count-to-select-cyo-button-variant'));
    const { loading, variables, data } = useSalesforcePersonalizedContentQuery({
        queryName: 'updateCyoButton',
        interactionName: `${brandData?.domain} - Get Campaign - PDP Add Count To Select CYO Button`,
        featureFlag: pdpCountCyoButtonFlag && !isForcePdpCountCyoButtonFlag,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const isCyoChooseButtonData = data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isCyoChooseButtonEnabled = isForcePdpCountCyoButtonFlag || isCyoChooseButtonData?.toLowerCase() === 'test';
    useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (typeof isCyoButtonData !== 'undefined' && typeof window !== 'undefined' && !window.wuCYOPdpSelectButtonEventFired && isCyoChooseButtonData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Add Count On Cyo Select Button',
                eventLabel: isCyoChooseButtonData?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_detail: isCyoChooseButtonData?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_element: 'Add Count On Cyo Select Button',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuCYOPdpSelectButtonEventFired = true;
        }
        return () => {
            window.wuCYOPdpSelectButtonEventFired = false;
        };
    }, [isCyoChooseButtonData]);

    // default color assigned when no brand code found
    const {
        buttonColorSelect = '#512C1C',
    } = byoTheme[brandCode] || {};

    const seed = useUIDSeed();

    const buildImgPath = (path, name) => `${path + name}x.jpg`;
    const buttonTitle = (options) => {
        if (isCyoChooseButtonEnabled) {
            return <span>Choose {options?.cyo?.maxQuantity}</span>;
        }
        return 'Select';
    };
    if (loading || variables.skip) {
        return <GenericSkeleton />;
    }
    const renderLayout = () => (
        skuData.map((options) => (
            <div key={seed(options)} className={style.scrollableSection} role="presentation">
                <div
                    role="presentation"
                    className={style.imageContainer}
                >
                    <ResponsiveImage

                        className={style.giftImage}
                        path={buildImgPath(options?.image?.path, options?.image?.name)}
                        alt={options?.name || 'selectedGift'}
                        params={{
                            desktop: 'height=196&width=179',
                            tablet: 'height=196&width=179',
                            mobile: 'height=196&width=179',
                        }}
                    />
                </div>
                <div
                    style={{
                        '--btn-selected-color': buttonColorSelect,
                    }}
                >
                    <button
                        id={` Add ${options?.name || 'Item'} to cart`}
                        data-testid="btn-add"
                        type="button"
                        className={`${style.buttonContainer} `}
                        onClick={() => {
                            selectProduct({
                                target: {
                                    value: options.id,
                                },
                            });
                            dispatch(trackEvent({
                                ga_event_name: 'byo_interaction',
                                eventCategory: 'BYO',
                                eventAction: 'Size selected',
                                eventLabel: `${options?.name} - ${options?.id}` || '',

                                interaction_type: 'select_item',
                                byo_step: '',
                                byo_item_name: options?.name,
                                byo_item_variant: options?.id,
                                byo_item_quantity: '',
                            }));
                        }}
                    >
                        {selectedItems?.selectedSku?.id === options?.id ? (
                            <div className={style.selectedButton}>
                                Selected
                                <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                            </div>
                        ) : (
                            buttonTitle(options)
                        )}
                    </button>
                    <span
                        className={style.giftName}

                    >
                        {ReactHtmlParser(options?.name?.toLowerCase())}
                    </span>
                    <span className={style.giftPrice}>
                        {renderSkuPrice(options, true)}
                    </span>
                </div>
            </div>

        ))
    );
    return (
        <>
            <Media query={{ maxWidth: 600 }} defaultMatches={deviceType === 'mobile'}>
                {(matches) => (matches ? <div className={style.itemsContainer}>{renderLayout()}</div>
                    : (
                        <Slider
                            ref={sliderRef}
                            draggable
                            className={style.sliderContainer}
                            dots={false}
                            infinite={false}
                            speed={500}
                            slidesToShow={3.5}
                            slidesToScroll={3}
                            arrows
                            style={{
                                '--btn-selected-color': buttonColorSelect,
                            }}

                            nextArrow={<div><svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M6.49 20.13l1.77 1.77 9.9-9.9-9.9-9.9-1.77 1.77L14.62 12l-8.13 8.13z" /></svg></div>}
                            prevArrow={<div><svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.51 3.87L15.73 2.1 5.84 12l9.9 9.9 1.77-1.77L9.38 12l8.13-8.13z" /></svg></div>}
                            responsive={[
                                {
                                    breakpoint: 600,
                                    settings: {
                                        slidesToShow: 2.75,
                                        slidesToScroll: 2,
                                    },
                                },
                            ]}
                        >
                            {renderLayout()}
                        </Slider>
                    ))}
            </Media>
        </>
    );
};

SelectGiftSize.propTypes = {
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    selectProduct: func.isRequired,
    renderSkuPrice: func,
};

SelectGiftSize.defaultProps = {
    renderSkuPrice: () => {},
};
export default SelectGiftSize;
